import React, { useEffect } from "react";
import { Container, Divider } from "@mui/material";
import { useTranslation } from "../../../hooks/useTranslation";
import { FieldValues } from "react-hook-form";
import { useForm } from "../../../hooks/useForm";
import { useMarketplaceStore } from "../../../store/useMarketplaceStore";
import { useCustom, useCustomMutation } from "@refinedev/core";
import { ActionButtons } from "../../../components/form/ActionButtons";
import { SettingsOption } from "../../../components/form/SettingsOption";
import { SyncrosaleLoader } from "../../../components/layout/components/SyncrosaleLoader";

type MarketplaceFilterSettingsProps = {
  settingsConfig: {
    baseSettings: (keyof FieldValues)[];
    thresholdMapping: Record<string, string | undefined>;
    defaultValues: FieldValues;
    url: string;
    translateKey: string;
    allowedValuesMapping?: Record<string, string[]>; // Add this line
  };
};

export const MarketplaceFilterSettings: React.FC<
  MarketplaceFilterSettingsProps
> = ({ settingsConfig }) => {
  const selectedCountry = useMarketplaceStore((state) => state.selectedCountry);
  const { translate } = useTranslation(
    `pages.storeSecurity.filters.${settingsConfig.translateKey}`,
  );

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty },
  } = useForm<FieldValues>({
    defaultValues: settingsConfig.defaultValues,
  });

  const { data, isLoading, isFetching } = useCustom({
    url: settingsConfig.url,
    method: "get",
    queryOptions: {
      enabled: !!selectedCountry,
      retry: (failureCount, error) => {
        if (error.status === 400 || error.status === 404) {
          reset(settingsConfig.defaultValues);
          return false;
        }
        return failureCount < 3;
      },
    },
    errorNotification: (data) => {
      if (data && (data.status === 400 || data.status === 404)) {
        return false;
      }
      return {
        message: data?.message ?? "test",
        description: "Error",
        type: "error",
      };
    },
  });

  useEffect(() => {
    const formValues = data?.data;
    if (!formValues) return;

    reset({ ...settingsConfig.defaultValues, ...formValues });
  }, [data, reset]);

  const { mutate, isLoading: isSaveLoading } = useCustomMutation();

  const onSubmit = async (data: FieldValues) => {
    const submittedData = { ...data };
    for (const [action, threshold] of Object.entries(
      settingsConfig.thresholdMapping,
    )) {
      const allowedValues = settingsConfig.allowedValuesMapping?.[action] || [];

      // Check if the action is in the allowed values
      if (allowedValues.includes(submittedData[action])) {
        // If the action is not allowed, remove the corresponding threshold
        if (threshold) {
          delete submittedData[threshold];
        }
      }
    }
    mutate(
      {
        url: settingsConfig.url,
        method: "put",
        values: submittedData,
        successNotification: () => ({
          message: translate("updatedSuccessfully"),
          type: "success",
        }),
      },
      {
        onSuccess: (data) => {
          reset({ ...settingsConfig.defaultValues, ...data?.data });
        },
      },
    );
  };

  if (isLoading || isFetching) {
    return <SyncrosaleLoader />;
  }

  return (
    <Container sx={{ padding: "16px", marginBottom: "16px" }}>
      <form onSubmit={handleSubmit(onSubmit)}>
        {settingsConfig.baseSettings.map((name) => {
          const label = `${name}.label`;
          const options = [];

          // Conditionally add options based on the name
          if (name === "unavailableProductsBlocking") {
            options.push(
              { value: "ALLOW", label: `${name}.setZeroStock` },
              { value: "REMOVE", label: `${name}.remove` },
            );
          } else {
            options.push(
              { value: "ALLOW", label: `${name}.allow` },
              { value: "SET_STOCK_ZERO", label: `${name}.setZeroStock` },
              { value: "REMOVE", label: `${name}.remove` },
            );
          }

          if (name === "restrictedProductAction") {
            options.push(
              { value: "REMOVE_BLOCK", label: `${name}.removeBlock` },
              {
                value: "REMOVE_BLOCK_BRAND",
                label: `${name}.removeBlockBrand`,
              },
            );
          }

          const thresholdName = settingsConfig.thresholdMapping[name];
          const allowedValues = settingsConfig.allowedValuesMapping?.[name]; // Get allowedValues from config

          return (
            <React.Fragment key={name}>
              <SettingsOption
                name={name}
                label={label}
                options={options}
                control={control}
                thresholdName={thresholdName}
                watch={watch}
                translate={translate}
                allowedValues={allowedValues} // Pass allowedValues to SettingsOption
              />
              <Divider sx={{ margin: "10px 0px" }} />
            </React.Fragment>
          );
        })}
        <ActionButtons
          isLoading={isSaveLoading}
          isDirty={isDirty}
          reset={reset}
        />
      </form>
    </Container>
  );
};
